/**
 * Customer Dashboard — Pass B (Rich UX).
 *
 * Layout:
 *   < md  : single column, stacked cards.
 *   >= md : 60/40 grid (3-col / 2-col within a 5-col canvas).
 *
 * Composition (in order):
 *   1. Hero band       — greeting + tier + lifetime points
 *   2. Points card     — large balance, progress to next tier
 *   3. Today at Napoli — DETERMINISTIC by weekday
 *   4. Next reward     — feature card with eligibility state
 *   5. Timeline        — recent 5 transactions (left col on desktop)
 *   6. Referral CTA    — bring a friend
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, ArrowRight, Lock, Gift, Clock, Users } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { SkeletonBlock } from "../../components/Skeleton";
import {
  tierLabel,
  tierTagline,
  dayGreeting,
  todayAtNapoli,
  relativeTime,
} from "../../lib/tier";

const Card = ({ className = "", testId, children }) => (
  <div
    data-testid={testId}
    className={[
      "relative bg-napoli-marble border border-napoli-gold/30 p-6",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

const Eyebrow = ({ children, className = "" }) => (
  <p
    className={`font-body text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood ${className}`}
  >
    {children}
  </p>
);

const ProgressBar = ({ percent, testId }) => (
  <div
    data-testid={testId}
    className="h-1.5 w-full bg-napoli-cream-warm overflow-hidden"
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={Math.round(percent)}
  >
    <div
      className="h-full bg-napoli-gold transition-[width] duration-700 ease-out"
      style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
    />
  </div>
);

const HeroBand = ({ user, tier }) => {
  const first = user?.first_name || "amico";
  return (
    <section
      data-testid="dashboard-hero"
      className="relative bg-napoli-espresso text-napoli-cream overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-napoli-oxblood/40 blur-3xl"
      />
      <div className="relative px-6 md:px-10 py-10 md:py-14 max-w-7xl mx-auto">
        <p className="font-flourish text-napoli-gold text-2xl md:text-3xl">Benvenuto</p>
        <h1
          data-testid="dashboard-greeting"
          className="mt-1 font-display font-bold text-3xl md:text-5xl leading-tight"
        >
          {dayGreeting(first)}
        </h1>
        <div className="mt-6 flex items-center flex-wrap gap-x-8 gap-y-3">
          <div className="flex items-center gap-3">
            <span
              data-testid="dashboard-tier-pill"
              className="inline-flex items-center gap-2 px-3 py-1 border border-napoli-gold/60 text-napoli-gold font-body text-[10px] tracking-[0.22em] uppercase"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-napoli-gold" aria-hidden />
              {tierLabel(tier?.current)}
            </span>
            <span className="font-body italic text-[12px] text-napoli-cream/70">
              {tierTagline(tier?.current)}
            </span>
          </div>
          <div className="flex items-center gap-2 text-napoli-cream/85">
            <Sparkles size={14} className="text-napoli-gold" />
            <span className="font-body text-[12px] tracking-[0.05em]">
              <strong className="text-napoli-cream">
                {(user?.lifetime_points ?? 0).toLocaleString()}
              </strong>{" "}
              points earned, all-time
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

const PointsCard = ({ user, tier }) => {
  const balance = user?.points_balance ?? 0;
  const next = tier?.next;
  const pctRaw = tier?.progress_percent;
  // The backend computes a forward-looking progress for the *next* tier. If
  // the user is already padrone we display a finished state instead of a bar.
  const pct = typeof pctRaw === "number" ? pctRaw : 0;
  return (
    <Card testId="dashboard-points-card" className="overflow-hidden">
      <Eyebrow>Your balance</Eyebrow>
      <div className="mt-3 flex items-end gap-3">
        <p
          data-testid="dashboard-points-balance"
          className="font-display font-bold text-6xl md:text-7xl text-napoli-oxblood leading-none"
        >
          {balance.toLocaleString()}
        </p>
        <p className="pb-2 font-body text-[10px] tracking-[0.3em] uppercase text-napoli-espresso-soft">
          Points
        </p>
      </div>
      <div className="mt-6 border-t border-napoli-gold/20 pt-5">
        {next ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="font-body text-[11px] tracking-[0.18em] uppercase text-napoli-espresso-soft">
                Toward {tierLabel(next)}
              </p>
              <p
                data-testid="dashboard-points-to-next"
                className="font-body font-semibold text-[12px] text-napoli-oxblood"
              >
                {tier.points_to_next} pts to go
              </p>
            </div>
            <ProgressBar percent={pct} testId="dashboard-tier-progress" />
            <p className="mt-3 font-body italic text-[12px] text-napoli-espresso-soft">
              You&rsquo;re {Math.round(pct)}% of the way to a new table.
            </p>
          </>
        ) : (
          <div className="flex items-center gap-3 text-napoli-gold">
            <Sparkles size={16} />
            <p className="font-body font-semibold text-[12px] tracking-[0.18em] uppercase">
              Padrone — the house is yours
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

const TodayBanner = () => {
  const t = useMemo(() => todayAtNapoli(), []);
  return (
    <Card
      testId="dashboard-today-banner"
      className="bg-napoli-oxblood text-napoli-cream border-napoli-oxblood-dark"
    >
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center bg-napoli-cream/10 border border-napoli-gold/40">
          <Clock size={18} className="text-napoli-gold" />
        </div>
        <div className="flex-1">
          <p className="font-flourish text-napoli-gold text-2xl leading-none">
            {t.eyebrow}
          </p>
          <p className="mt-1 font-body text-[10px] tracking-[0.28em] uppercase text-napoli-cream/70">
            Today at Napoli
          </p>
          <p
            data-testid="dashboard-today-body"
            className="mt-3 font-display font-semibold text-lg md:text-xl leading-snug"
          >
            {t.body}
          </p>
        </div>
      </div>
    </Card>
  );
};

const NextRewardCard = ({ reward }) => {
  if (!reward) {
    return (
      <Card testId="dashboard-next-reward" className="flex items-center gap-4">
        <div className="h-12 w-12 flex items-center justify-center bg-napoli-cream-warm border border-napoli-gold/30">
          <Gift size={20} className="text-napoli-oxblood" />
        </div>
        <div>
          <Eyebrow>Your next reward</Eyebrow>
          <p className="mt-1 font-body text-[13px] text-napoli-espresso-soft">
            New rewards hit the catalog soon. Visit, earn, and we&rsquo;ll have
            something waiting.
          </p>
        </div>
      </Card>
    );
  }
  const eligible = !!reward.eligible;
  const pct = Math.max(0, Math.min(100, reward.progress_to_eligible || 0));
  return (
    <Card testId="dashboard-next-reward">
      <Eyebrow>Your next reward</Eyebrow>
      <div className="mt-3 flex gap-4">
        <div
          className={[
            "relative h-24 w-24 flex-shrink-0 bg-cover bg-center border border-napoli-gold/30",
            !eligible && "opacity-80",
          ].join(" ")}
          style={
            reward.image_url
              ? { backgroundImage: `url(${reward.image_url})` }
              : { backgroundColor: "#EFE3CE" }
          }
          aria-hidden
        >
          {!eligible ? (
            <span className="absolute inset-0 bg-napoli-espresso/35 flex items-center justify-center">
              <Lock size={18} className="text-napoli-cream" />
            </span>
          ) : null}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-lg text-napoli-espresso truncate">
            {reward.name}
          </p>
          <p className="mt-0.5 font-body text-[12px] text-napoli-espresso-soft line-clamp-2">
            {reward.description}
          </p>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-body font-semibold text-[12px] text-napoli-oxblood">
                {reward.points_cost} pts
              </span>
              <span className="font-body text-[10px] tracking-[0.18em] uppercase text-napoli-espresso-soft">
                {eligible
                  ? "Available now"
                  : reward.reason_ineligible === "tier_locked"
                  ? `${tierLabel(reward.tier_required)} tier`
                  : `${reward.points_short} pts to go`}
              </span>
            </div>
            <ProgressBar percent={eligible ? 100 : pct} />
          </div>
        </div>
      </div>
    </Card>
  );
};

const Timeline = ({ items }) => (
  <Card testId="dashboard-timeline">
    <div className="flex items-center justify-between">
      <Eyebrow>Recent activity</Eyebrow>
      <Link
        to="/customer/history"
        data-testid="dashboard-timeline-view-all"
        className="font-body text-[11px] tracking-[0.18em] uppercase text-napoli-oxblood hover:text-napoli-oxblood-dark"
      >
        View all
      </Link>
    </div>
    {items.length === 0 ? (
      <div className="mt-6 text-center py-6 border border-dashed border-napoli-gold/30 bg-napoli-cream-warm/40">
        <p className="font-flourish text-napoli-oxblood text-2xl">benvenuto</p>
        <p className="mt-1 font-body italic text-[13px] text-napoli-espresso-soft">
          Your story starts on your first visit.
        </p>
      </div>
    ) : (
      <ol className="mt-5 relative" data-testid="dashboard-timeline-list">
        <span
          aria-hidden
          className="absolute left-[5px] top-2 bottom-2 w-px bg-napoli-gold/30"
        />
        {items.map((t) => {
          const positive = t.points_delta >= 0;
          return (
            <li
              key={t.id}
              data-testid={`dashboard-tx-${t.id}`}
              className="relative pl-6 pb-4 last:pb-0"
            >
              <span
                aria-hidden
                className={[
                  "absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-napoli-marble",
                  positive ? "bg-napoli-gold" : "bg-napoli-oxblood",
                ].join(" ")}
              />
              <div className="flex justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-body text-[13px] text-napoli-espresso truncate">
                    {t.notes ||
                      (positive ? "Visit at Napoli" : "Reward redeemed")}
                  </p>
                  <p className="font-body text-[11px] text-napoli-espresso-soft">
                    {relativeTime(t.created_at)}
                  </p>
                </div>
                <p
                  className={[
                    "font-body font-semibold text-[13px] whitespace-nowrap",
                    positive ? "text-napoli-gold" : "text-napoli-oxblood",
                  ].join(" ")}
                >
                  {positive ? "+" : ""}
                  {t.points_delta} pts
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    )}
  </Card>
);

const ReferralCta = () => {
  const navigate = useNavigate();
  return (
    <Card
      testId="dashboard-referral-cta"
      className="bg-napoli-cream-warm border-napoli-gold/40"
    >
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center bg-napoli-marble border border-napoli-gold/30">
          <Users size={18} className="text-napoli-oxblood" />
        </div>
        <div className="flex-1">
          <Eyebrow>Bring a friend</Eyebrow>
          <p className="mt-2 font-display font-semibold text-lg text-napoli-espresso leading-snug">
            Pull up another chair.
          </p>
          <p className="mt-1 font-body text-[12px] text-napoli-espresso-soft">
            They get a welcome plate. You get 100 points the night they show up.
          </p>
          <button
            type="button"
            onClick={() => navigate("/customer/profile")}
            data-testid="dashboard-referral-cta-btn"
            className="mt-4 inline-flex items-center gap-2 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.18em] uppercase px-5 py-3 hover:bg-napoli-oxblood-dark transition-colors"
          >
            Get my referral code
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </Card>
  );
};

const DashboardSkeleton = () => (
  <div data-testid="dashboard-skeleton" className="space-y-8">
    <SkeletonBlock className="h-44 md:h-56 border-0" />
    <div className="px-6 md:px-10 max-w-7xl mx-auto md:grid md:grid-cols-5 md:gap-8 space-y-6 md:space-y-0">
      <div className="md:col-span-3 space-y-6">
        <SkeletonBlock className="h-48" />
        <SkeletonBlock className="h-32" />
        <SkeletonBlock className="h-64" />
      </div>
      <div className="md:col-span-2 space-y-6">
        <SkeletonBlock className="h-40" />
        <SkeletonBlock className="h-40" />
      </div>
    </div>
  </div>
);

const CustomerDashboardPage = () => {
  const { user: authUser } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/customer/dashboard");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail?.error || "load_failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <div className="px-6 py-12 max-w-7xl mx-auto text-center font-body text-napoli-oxblood-dark">
        Couldn&rsquo;t load your dashboard. Refresh in a moment.
      </div>
    );
  }
  if (!data) return <DashboardSkeleton />;

  const { user, tier, next_reward, recent_transactions } = data;
  const mergedUser = { ...(authUser || {}), ...(user || {}) };

  return (
    <div data-testid="customer-dashboard">
      <HeroBand user={mergedUser} tier={tier} />
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-7xl mx-auto md:grid md:grid-cols-5 md:gap-10 space-y-6 md:space-y-0">
        {/* Left column 60% — points, today, timeline */}
        <div className="md:col-span-3 space-y-6">
          <PointsCard user={mergedUser} tier={tier} />
          <TodayBanner />
          <Timeline items={recent_transactions || []} />
        </div>
        {/* Right column 40% — next reward, referral */}
        <div className="md:col-span-2 space-y-6">
          <NextRewardCard reward={next_reward} />
          <ReferralCta />
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboardPage;
